import {
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import {
	CellHoverChangedEvent,
	OptionChangedEvent,
	RowClickEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	debounceTime,
	delay,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { ElementPermission } from '../../../core/domain/permission-types';
import {
	DcDxGrids,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZES,
	DxFilterField,
	DxOperations,
	DxSortField,
	StringFilterOperations,
} from '../../../core/dx-types';
import {
	DxListsStorage,
	IDxList,
	StoredColumn,
} from '../../../core/services/dx-lists.storage';
import { DxGridUtil } from '../../../core/utils/dx-grid.util';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { GroupingTagItemEntity } from '../../domain/grouping-tag-item.entity';
import {
	createGroupingTagSuccess,
	deleteGroupingTag,
	deleteGroupingTagSuccess,
	fetchProjectCountByGroupingTag,
	fetchReferencedProjectsByGroupingTagId,
	getGroupingTagsPage,
	getGroupingTagsPageSuccess,
	GroupingTagSelector,
	updateGroupingTag,
	updateGroupingTagSuccess,
} from '../../store';
import {
	ConfigPopupComponent,
	GroupingTagConfigPopupData,
} from '../config-popup/config-popup.component';
import {
	GroupingTagActionConfirmPopupComponent,
	GroupingTagActionEnum,
	GroupingTagConfirmationModalData,
} from '../grouping-tag-action-confirm-popup/grouping-tag-action-confirm-popup.component';
import { DxGroupingTagDataProvider } from './dx-data-provider.service';
import {
	GroupingTagListEntity,
	IGroupingTagListExtra,
} from './grouping-tag-list.entity';
import { GroupingTagListProvider } from './grouping-tag-list.provider';

type GroupingTagOverlay = {
	groupingTagId: string | undefined;
};

@Component({
	selector: 'app-grouping-tag-list',
	templateUrl: './grouping-tag-list.component.html',
	styleUrls: ['./grouping-tag-list.component.scss'],
	providers: [GroupingTagListProvider, DxGroupingTagDataProvider],
})
export class GroupingTagListComponent
	extends DcBaseComponent
	implements OnInit
{
	@Output()
	public triggerAddElement = new EventEmitter<void>();

	protected readonly HdIcons = HdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DxOperations = DxOperations;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly ActionIcons = ActionIcons;
	protected readonly ElementPermission = ElementPermission;
	protected readonly StringFilterOperations = StringFilterOperations;

	public allowedPageSizes = [DEFAULT_PAGE_SIZES[0]];
	public currentlyHovered = -1;
	public selectionCount = 0;
	public panelActions: Array<PanelAction> = [];
	public toggleActions = false;

	public dataSource: CustomStore | null = null;

	public vo$: Observable<{
		list: GroupingTagListEntity;
		size: number;
		page: number;
		hasCreatePermission: boolean;
		openedOverlay: GroupingTagOverlay;
	}>;

	public loadState = (): IDxList | undefined => {
		const dxList = this.dxStorage.getListState(this.dxLocalStorageKey);

		if (dxList?.columns) {
			const sortedFields = DxGridUtil.generateSortFields(dxList.columns);
			this.sortedFields$.next(sortedFields);
		}
		this.onGridReady$.next();
		return this.dxStorage.getListState(this.dxLocalStorageKey, false);
	};

	public saveState = async (state: IDxList): Promise<void> => {
		const page = await firstValueFrom(this.page$);

		if (page !== state.pageIndex) {
			this.page$.next(state.pageIndex);
		}

		this.dxStorage.setListState(
			this.dxLocalStorageKey,
			{
				...state,
				searchText: '',
			},
			false
		);
	};

	@ViewChild(DxDataGridComponent)
	public readonly grid: DxDataGridComponent | null = null;

	private readonly page$ = new BehaviorSubject<number>(DEFAULT_PAGE_INDEX);
	private readonly size$ = new BehaviorSubject<number>(DEFAULT_PAGE_SIZES[0]);
	private readonly search$ = new BehaviorSubject<string>('');
	private readonly filteredFields$ = new BehaviorSubject<
		Array<DxFilterField<GroupingTagItemEntity>>
	>([]);
	private readonly sortedFields$ = new BehaviorSubject<Array<DxSortField>>([]);
	private readonly filterPanelEnabled$ = new BehaviorSubject<boolean>(true);

	private readonly onGridReady$ = new Subject<void>();
	private readonly manualTrigger$ = new BehaviorSubject<boolean>(false);
	private readonly isContentReadySubject$ = new Subject<boolean>();

	private readonly openedOverlaySubject =
		new BehaviorSubject<GroupingTagOverlay>({
			groupingTagId: undefined,
		});

	public constructor(
		private readonly selector: GroupingTagSelector,
		private readonly store: Store,
		private readonly listProvider: GroupingTagListProvider,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		private readonly dataProvider: DxGroupingTagDataProvider,
		private readonly action$: Actions,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'grouping-tag-list';
		this.dxLocalStorageKey = DcDxGrids.GroupingTagListStandalone;
		this.dataSource = this.dataProvider.createDxStore();

		const list$ = this.selector.getGroupingTagItems$().pipe(
			withLatestFrom(this.selector.getGroupingTagTotalCount$()),
			map(([elements, total]) =>
				this.listProvider.setListElements(elements, total)
			),
			tap((list) => {
				this.grid?.instance.refresh(true);
				this.dataProvider.updateData(list);
			})
		);

		this.vo$ = combineAll({
			list: list$,
			size: this.size$,
			page: this.page$,
			hasCreatePermission: false,
			openedOverlay: this.openedOverlaySubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
		this.handleSubscription();
	}

	public triggerAddNewGroupingTag(): void {
		this.triggerAddElement.emit();
	}

	public async editGroupingTag(
		id: string,
		label: string,
		color: string
	): Promise<void> {
		const modalRef = this.modalService.openPopup<GroupingTagConfigPopupData>(
			ConfigPopupComponent,
			{
				title: $localize`:@@groupingTag.create.modal.title:`,
				subTitle: '',
				id,
				label,
				color,
			},
			{
				disableClose: true,
			}
		);

		const modalConfirmResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalConfirmResult) {
			return;
		}
		this.store.dispatch(
			updateGroupingTag({
				id,
				color: modalConfirmResult.color,
				label: modalConfirmResult.label,
			})
		);
	}

	public async rowSelected(e: RowClickEvent): Promise<void> {
		const grid = e.component;

		if (grid.isRowSelected(e.key)) {
			await grid.deselectRows([e.key]);
		} else {
			await grid.selectRows([e.key], true);
		}
	}

	public async deleteSelected(
		selectedItem: GroupingTagItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<string>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRows =
				this.grid.instance.getSelectedRowsData() as Array<GroupingTagItemEntity>;

			selected = selectedRows.map((r) => r.id);
			elementLabel = selectedRows.length === 1 ? selectedRows[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		const modalRef =
			this.modalService.openPopup<GroupingTagConfirmationModalData>(
				GroupingTagActionConfirmPopupComponent,
				{
					title: $localize`:@@groupingTag.action.confirmation.popup.delete.header:`,
					subTitle: '',
					groupingTagId: selected.length === 1 ? selected[0] : selected,
					element: elementLabel,
					action: GroupingTagActionEnum.Delete,
				},
				{
					disableClose: true,
				}
			);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		if (!selectedItem) {
			this.grid?.instance.clearSelection();
		}
		return this.store.dispatch(
			deleteGroupingTag({
				selected,
			})
		);
	}

	public refresh(): void {
		this.manualTrigger$.next(true);
	}

	public closePopover(): void {
		this.openedOverlaySubject.next({
			groupingTagId: undefined,
		});
	}

	public async openPopover(
		event: Event,
		groupingTag: GroupingTagItemEntity
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlaySubject);
		if (previous.groupingTagId === groupingTag.id) {
			this.openedOverlaySubject.next({
				...previous,
				groupingTagId: undefined,
			});
			return;
		}

		this.openedOverlaySubject.next({
			...previous,
			groupingTagId: groupingTag.id,
		});

		this.store.dispatch(
			fetchReferencedProjectsByGroupingTagId({
				id: groupingTag.id,
			})
		);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		const searchValue = searchTerm.trim();
		this.grid.instance.searchByText(searchValue);
		this.search$.next(searchValue);
	}

	public onSelectionChange(event: SelectionChangedEvent): void {
		this.toggleActions = event.selectedRowsData.length > 0;
		this.selectionCount = event.selectedRowsData.length;
	}

	public onGridContentReady(): void {
		this.isContentReadySubject$.next(true);
	}

	public onGridOptionChange(event: OptionChangedEvent): void {
		if (event.name === 'hoveredElement') {
			return;
		}

		if (
			event.name !== 'paging' &&
			event.name !== 'filterValue' &&
			event.name !== 'filterPanel' &&
			event.name !== 'columns'
		) {
			return;
		}

		const { fullName: action, value } = event;

		if (action.includes('pageSize')) {
			this.page$.next(DEFAULT_PAGE_INDEX);
			this.size$.next(value);
		} else if (action.includes('pageIndex')) {
			this.page$.next(value);
		} else if (action === 'filterValue') {
			let filteredFields: Array<DxFilterField<GroupingTagItemEntity>> = [];
			if (value) {
				filteredFields = DxGridUtil.parseFilters<GroupingTagItemEntity>(value);
			}

			this.filteredFields$.next(filteredFields);
			this.page$.next(DEFAULT_PAGE_INDEX);
		} else if (action.includes('columns') && action.endsWith('sortOrder')) {
			const columns = event.component.getVisibleColumns() as Array<
				Partial<StoredColumn>
			>;

			const sortedFields = DxGridUtil.generateSortFields(columns);

			this.sortedFields$.next(sortedFields);
		} else if (action.includes('filterEnabled')) {
			this.filterPanelEnabled$.next(value);
		}
	}

	private triggerInit(): void {
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					let cols = this.listProvider.getAvailableColumns();
					cols = this.dxStorage.synchronize<
						IGroupingTagListExtra,
						GroupingTagItemEntity
					>(this.dxLocalStorageKey, cols);

					this.listProvider.setList(cols);

					this.bootstrapIcons();
				})
			)
			.subscribe();
	}

	private handleSubscription(): void {
		this.action$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					createGroupingTagSuccess,
					deleteGroupingTagSuccess,
					updateGroupingTagSuccess
				),
				tap(() => {
					this.modalService.closeActiveModal({
						reset: true,
					});
					this.manualTrigger$.next(true);
				})
			)
			.subscribe();

		this.onGridReady$
			.pipe(
				takeUntil(this.onDestroy$),
				switchMap(() =>
					combineAll({
						manualTrigger: this.manualTrigger$,
						search: this.search$.pipe(debounceTime(300)),
						gridState: combineAll({
							filteredFields: this.filteredFields$,
							sortedFields: this.sortedFields$,
							page: this.page$,
							size: this.size$,
							filterPanelEnabled: this.filterPanelEnabled$,
						}).pipe(debounceTime(500)),
					})
				),
				takeUntil(this.onDestroy$),
				tap(({ search, gridState }) => {
					const {
						filteredFields,
						sortedFields,
						page,
						size,
						filterPanelEnabled,
					} = gridState;
					this.store.dispatch(
						getGroupingTagsPage({
							size,
							page,
							search,
							filteredFields: filterPanelEnabled
								? filteredFields
								: new Array<DxFilterField<GroupingTagItemEntity>>(),
							sortedFields,
						})
					);
				})
			)
			.subscribe();

		this.onGridReady$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(500),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();

		const onGridRead$ = this.action$.pipe(
			takeUntil(this.onDestroy$),
			ofType(getGroupingTagsPageSuccess)
		);
		merge(onGridRead$, this.manualTrigger$)
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(720),
				filter(() => this.grid !== null),
				tap(() => {
					const items = this.grid?.instance
						?.getDataSource()
						?.items() as Array<GroupingTagItemEntity>;
					const ids = items?.map((it) => `${it.id}`) ?? [];
					if (ids.length !== 0) {
						this.store.dispatch(
							fetchProjectCountByGroupingTag({
								ids,
							})
						);
					}
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.Delete,
			ActionIcons.Share,
			ActionIcons.ShareOff,
			ActionIcons.Output,
			ActionIcons.Input,
			ActionIcons.Publish,
			ActionIcons.ColumnExpositionChooser,
		];

		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:@@groupingTag.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: $localize`:@@groupingTag.list.action.delete:`,
				typed: 'plain-context-action',
			},
		];
	}
}
